import React from 'react';
import { Calendar, Sparkles } from 'lucide-react';
import { aaiApi } from '../../../services/aaiApi';
import type { MySessions } from '../../../types/aai';
import { useAsync } from '../useAsync';
import { Loading, ErrorNote } from '../components';
import { Card, MonoLabel } from '../dashboards/ui';

const TYPE_LABEL: Record<string, string> = { coaching: 'Coaching', 'quick-win': 'Quick win', 'deep-dive': 'Deep dive' };

const MySessionsPage: React.FC = () => {
  const { data, loading, error } = useAsync<MySessions>(() => aaiApi.getMySessions(), 'Could not load your sessions.');
  if (loading) return <Loading label="Loading your sessions…" />;
  if (error || !data) return <ErrorNote message={error || 'Something went wrong.'} />;

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center gap-2 mb-1"><Calendar size={22} className="text-brand-500" /><h1 className="text-2xl font-semibold text-gray-900">My Sessions</h1></div>
      <p className="text-sm text-gray-500 mb-6">Your upcoming coaching sessions and the skills you've earned.</p>

      <Card className="mb-4">
        <MonoLabel className="mb-3">Upcoming</MonoLabel>
        {data.upcoming.length === 0 ? <p className="text-[13px] text-gray-400">No sessions scheduled yet — your coach will set these up.</p> : (
          <div className="space-y-2">
            {data.upcoming.map((s) => (
              <div key={s.id} className="bg-white rounded-lg px-3.5 py-3 flex items-center justify-between">
                <div>
                  <div className="font-semibold text-[13.5px]">{s.title}</div>
                  <div className="text-[11.5px] text-gray-500">{TYPE_LABEL[s.session_type] || s.session_type}{s.duration_min ? ` · ${s.duration_min} min` : ''}{s.cohort_name ? ` · ${s.cohort_name}` : ''}{s.module_week ? ` · ${s.module_week}` : ''}</div>
                </div>
                {s.scheduled_at && <span className="text-[12px] font-medium text-brand-600">{new Date(s.scheduled_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>}
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card>
        <MonoLabel className="mb-3">Skills you've earned</MonoLabel>
        {data.skills.length === 0 ? <p className="text-[13px] text-gray-400">Attend a session to earn your first skill.</p> : (
          <div className="space-y-2">
            {data.skills.map((sk, i) => (
              <div key={i} className="flex items-center gap-2.5 bg-white rounded-lg px-3 py-2">
                <span className="w-7 h-7 rounded-md bg-accent-100 text-amber-700 grid place-items-center"><Sparkles size={14} /></span>
                <span className="text-[13px] font-medium">{sk.name}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default MySessionsPage;
